// eslint-disable no-console
import React, { createElement, useEffect, useState } from 'react';
import { MoreVert } from '@material-ui/icons';
import { TableCell, TableRow, withStyles } from '@material-ui/core';
import clsx from 'clsx';
import { Link } from 'react-router-dom';

import Tooltip from '../../../../Tooltip';
import Image from '../../../../Image';
import Box from '../../../../atomic/Box';
import IconButton from '../../../../atomic/IconButton';
import TextHighlighted from '../../../../TextHighlighted';
import LockForDocView from '../../../../../assets/icons/LockForDocView';

import CheckboxCell from '../CheckboxCell';
import FileImageIcon from '../../../../../assets/icons/DocumentViewIcons/FileImageIcon';
import {
  getDocumentCompanyName,
  getDocumentCreatedDate,
  getDocumentEvents,
  getDocumentFileType,
  getDocumentId,
  getDocumentIsBuilding,
  getDocumentName,
  getDocumentOwnedByName,
  getDocumentProfessionTypeForDocumentView,
  getDocumentRooms,
  getDocumentType,
  getFileThumbNailForDocumentView,
  getOriginalDocumentPath,
  isDocumentImage,
  isDocumentReadOnly,
} from '../../../../../utils/documents';
import { formattedDate } from '../../../../../utils/dates';

import useDashboardLinkPrefix from '../../../../../hooks/useDashboardLinkPrefix';
import styles from './style';
import BuildingDocument from '../../../../../assets/icons/BuildingDocument';
import HouseIcon from '../../../../../assets/icons/HouseIcon';
import EventBar from '../../../../../assets/icons/EventBar';

const TableRowItem = ({
  classes,
  document,
  newUpload,
  searchQuery,
  actionsEnabled,
  onDocumentClick,
  onSelect = () => {},
  reset = false,
  selectedDocuments = [],
  showBulkSelectionOptions,
  isPersonalDocuments,
  isFromPersonal,
}) => {
  const [options, setOptions] = useState(selectedDocuments);
  const [checkDocumentItem, setCheckDocumentItem] = useState(null);

  const dashboardLinkPrefix = useDashboardLinkPrefix();
  const professionType = getDocumentProfessionTypeForDocumentView(document);
  const documentPath = `${dashboardLinkPrefix}/documents/${getDocumentId(
    document
  )}`;

  const docTitle = getDocumentName(document);
  const avsenderTitle =
    getDocumentOwnedByName(document) || getDocumentCompanyName(document);

  const personalDocumentPath = `/personal-documents/${getDocumentId(document)}`;

  const handleChange = (o) => {
    if (checkDocumentItem) {
      setCheckDocumentItem(null);
      onSelect(o);
    } else {
      setCheckDocumentItem(o);
      onSelect(o);
    }
  };
  useEffect(() => {
    onSelect(options);
  }, [options.length]);
  useEffect(() => {
    if (reset) {
      setOptions([]);
    }
  }, [reset]);

  const renderIcon = () => {
    if (isDocumentImage(document)) {
      return (
        <>
          <Image
            fallbackComponent={<FileImageIcon className={classes.imageIcon} />}
            alt={docTitle}
            src={getOriginalDocumentPath(document)}
            className={classes.thumbnailImageIcon}
          />
        </>
      );
    }
    return createElement(
      getFileThumbNailForDocumentView(getDocumentFileType(document)) ||
        getFileThumbNailForDocumentView('default'),
      { className: classes.imageIcon }
    );
  };

  const getEventAndRoomCard = (values, type) =>
    values?.length > 0 && (
      <Tooltip
        interactive
        placement="bottom"
        arrow
        popperArrow
        title={values?.map(
          (tooltipValue, index) =>
            ` ${tooltipValue?.title}${index !== values?.length - 1 ? ',' : ''}`
        )}
      >
        <div
          className={clsx(
            classes.eventAndRoomCard,
            classes.moreEventAndRoomCard
          )}
        >
          {type === 'room' && <HouseIcon />}
          {type === 'event' && <EventBar />}
          <span>{values?.length}</span>
        </div>
      </Tooltip>
    );

  const TooltipForDocNameAndAsvender = ({ title, children }) =>
    title?.length > 50 ? (
      <Tooltip title={title} interactive placement="bottom" arrow popperArrow>
        {children}
      </Tooltip>
    ) : (
      children
    );

  const handleDocumentSelect = () => {
    handleChange(document);
  };

  return (
    <TableRow className={classes.rowHover}>
      <CheckboxCell
        onChange={handleDocumentSelect}
        disabled={
          isDocumentReadOnly(document) || getDocumentIsBuilding(document)
        }
        isVisible={showBulkSelectionOptions}
        isChecked={
          selectedDocuments.filter((value) => value.id === document?.id)
            .length > 0
        }
        style={{ padding: 0 }}
      />

      <TableCell className={clsx(classes.tableCell, classes.tableCellDocName)}>
        <Box
          className={clsx(
            classes.displayFlex,
            classes.alignItemsCenter,
            !showBulkSelectionOptions ? classes.marginLeft9 : null
          )}
        >
          <span
            className={clsx([
              classes.imageIconBox,
              getDocumentFileType(document),
            ])}
          >
            {renderIcon()}
          </span>
          {isDocumentReadOnly(document) && (
            <Tooltip
              title="Filen inneholder historisk data, og kan ikke endres"
              interactive
              placement="bottom"
              arrow
              popperArrow
            >
              <div className={classes.lockForDocViewContainer}>
                <LockForDocView
                  width={18}
                  height={18}
                  className={classes.lockForDocView}
                />
              </div>
            </Tooltip>
          )}

          {getDocumentIsBuilding(document) && (
            <Tooltip
              title="Filen er knyttet til en annen bolig"
              interactive
              placement="bottom"
              arrow
              popperArrow
            >
              <div className={classes.builidingDocViewContainer}>
                <BuildingDocument
                  width={14}
                  height={14}
                  className={classes.lockForDocView}
                />
              </div>
            </Tooltip>
          )}

          <TooltipForDocNameAndAsvender title={docTitle}>
            <Link
              to={{
                pathname: isFromPersonal ? personalDocumentPath : documentPath,
                state: { newUpload, isPersonalDocuments, isFromPersonal },
              }}
              className={clsx(
                !isDocumentReadOnly(document) ? classes.docNameLink : null,
                classes.docNameText
              )}
            >
              <TextHighlighted searchQuery={searchQuery}>
                {docTitle}
              </TextHighlighted>
            </Link>
          </TooltipForDocNameAndAsvender>
        </Box>
      </TableCell>

      <TableCell className={clsx([classes.dateCell, classes.tableCell])}>
        <TextHighlighted searchQuery={searchQuery}>
          {formattedDate(getDocumentCreatedDate(document), 'D.MM.YYYY')}
        </TextHighlighted>
      </TableCell>

      {!isPersonalDocuments && (
        <>
          <TableCell className={clsx(classes.tableCell, classes.typeCell)}>
            <TextHighlighted searchQuery={searchQuery}>
              {getDocumentType(document)}
            </TextHighlighted>
          </TableCell>

          <TableCell
            className={clsx([classes.tableCell, classes.avsenderCell])}
          >
            <TooltipForDocNameAndAsvender title={avsenderTitle}>
              <TextHighlighted searchQuery={searchQuery}>
                <span>{avsenderTitle}</span>
              </TextHighlighted>
            </TooltipForDocNameAndAsvender>
          </TableCell>

          <TableCell
            className={clsx([
              classes.cellMinWidth270,
              classes.combinedBodyCell,
              classes.tableCell,
            ])}
          >
            <div className={classes.combinedWrapper}>
              <TextHighlighted searchQuery={searchQuery}>
                <span
                  className={professionType ? classes.eventAndRoomCard : null}
                >
                  {professionType}
                </span>
                {getEventAndRoomCard(getDocumentRooms(document), 'room')}
                {getEventAndRoomCard(getDocumentEvents(document), 'event')}
              </TextHighlighted>
            </div>
          </TableCell>
        </>
      )}
      <TableCell className={clsx([classes.tableCell, classes.optionsCell])}>
        {actionsEnabled && (
          <IconButton
            size="small"
            onClick={(event) => onDocumentClick(event, document)}
          >
            <MoreVert className={classes.moreIcon} />
          </IconButton>
        )}
      </TableCell>
    </TableRow>
  );
};

export default withStyles(styles)(TableRowItem);
